import React, { useEffect, useState } from 'react';
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  useMapEvent,
  } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  } from 'reactstrap';
import { getStaticData } from '../services/data.service';
import {
  EVIDENCE_LEVELS,
  getDiseaseEvidence,
  getExposureWarnings,
  refreshDiseaseEvidence,
  refreshExposureWarnings,
  saveDiseaseEvidence,
  warningActionText,
  submitDiseaseEvidenceToAccount,
  } from '../services/exposure.service';
import {
  CONSENT_SCOPES,
  getConsentGrants,
  getPrivacySettings,
  refreshConsentGrants,
  revokeConsentGrant,
  savePrivacySettings,
  } from '../services/privacy.service';
import {
  getPrivateStayEvents,
  startForegroundStayDetection,
  stopForegroundStayDetection,
  STAY_PERSISTED_EVENT,
  addManualStayEvent,
  getCurrentPositionForManualStay,
} from '../services/location-tracking.service';
import { uploadPendingStayEvents } from '../services/stay-upload.service';

const evidenceLabels = {
  [EVIDENCE_LEVELS.SELF_REPORTED]: 'Self reported',
  [EVIDENCE_LEVELS.DOCUMENT_UPLOADED]: 'Evidence uploaded (not yet verified)',
  [EVIDENCE_LEVELS.DOCUMENT_VERIFIED]: 'Uploaded evidence verified',
  [EVIDENCE_LEVELS.CLINICIAN_CONFIRMED]: 'Confirmed by clinician',
  [EVIDENCE_LEVELS.LAB_CONFIRMED]: 'Laboratory confirmed',
};

function ManualStayMapSelection({
  point,
  onSelect
}) {
  useMapEvent(
    'click',
    event => {
      onSelect({
        latitude: event.latlng.lat,
        longitude: event.latlng.lng,
        accuracy: null
      });
    }
  );

  if (!point) {
    return null;
  }

  return (
    <CircleMarker
      center={[
        point.latitude,
        point.longitude
      ]}
      radius={8}
    />
  );
}

function localDateTimeValue(date) {
  const pad =
    value =>
      String(value).padStart(2, '0');

  return (
    `${date.getFullYear()}-` +
    `${pad(date.getMonth() + 1)}-` +
    `${pad(date.getDate())}T` +
    `${pad(date.getHours())}:` +
    `${pad(date.getMinutes())}`
  );
}

function retentionLabel(hours) {
  const value = Number(hours);

  if (value === 24) {
    return '1 day';
  }

  if (value % 24 === 0) {
    return `${value / 24} days`;
  }

  return `${value} hours`;
}

export default function HealthSafety() {
  const [privacy, setPrivacy] = useState(getPrivacySettings());
  const [warnings, setWarnings] = useState(getExposureWarnings());
  const [evidence, setEvidence] = useState(getDiseaseEvidence());
  const [consents, setConsents] = useState(getConsentGrants());
  const [stays, setStays] = useState(getPrivateStayEvents());
  const [message, setMessage] = useState('');
  const [selectedDisease, setSelectedDisease] = useState('');

  const [showManualStay, setShowManualStay] =
    useState(false);

  const [manualPoint, setManualPoint] =
    useState(null);

  const [manualStart, setManualStart] =
    useState(() =>
      localDateTimeValue(
        new Date(
          Date.now() -
          60 * 60 * 1000
        )
      )
    );

  const [manualEnd, setManualEnd] =
    useState(() =>
      localDateTimeValue(
        new Date()
      )
    );

  const [submittingEvidenceId, setSubmittingEvidenceId] = useState(null);
  const [staticData] = getStaticData();
  const diseases =
    staticData && staticData.diseases
      ? staticData.diseases
      : [];

  const diseaseList =
    Object.values(diseases || {})
      .filter(Boolean);

  const pendingStayCount =
    (stays || []).filter(
      stay => !stay.uploaded
    ).length;

  const uploadedStayCount =
    (stays || []).filter(
      stay => !!stay.uploaded
    ).length;

  const exposureProtectionEnabled =
    !!privacy.shareExposureStays;

  const activeConsents =
    (consents || []).filter(
      grant => {
        const revoked =
          grant.revokedAt ||
          grant.revoked_at;

        if (revoked) {
          return false;
        }

        const expiry =
          grant.expiresAt ||
          grant.expires_at;

        if (!expiry) {
          return true;
        }

        const expiryTime =
          new Date(
            expiry
          ).getTime();

        return (
          Number.isFinite(
            expiryTime
          ) &&
          expiryTime >
            Date.now()
        );
      }
    );

  const recentStays =
    [...(stays || [])]
      .sort(
        (a, b) =>
          new Date(b.endedAt || 0) -
          new Date(a.endedAt || 0)
      )
      .slice(0, 5);

  useEffect(() => {
    let mounted = true;

    refreshExposureWarnings()
      .then(result => {
        if (!mounted) return;

        setWarnings(
          Array.isArray(result)
            ? result
            : getExposureWarnings()
        );
      })
      .catch(() => {
        if (!mounted) return;

        setMessage(
          'Your exposure alerts could not be refreshed right now.'
        );
      });

    return () => {
      mounted = false;
    };
  }, []);


  useEffect(() => {
    let mounted = true;

    refreshDiseaseEvidence()
      .then(rows => {
        if (mounted) {
          setEvidence(rows);
        }
      })
      .catch(() => {
        // Keep the local evidence cache visible.
      });

    refreshConsentGrants()
      .then(rows => {
        if (mounted) {
          setConsents(rows);
        }
      })
      .catch(() => {
        // Keep the last known consent cache visible.
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Resume foreground stay detection when the user has
  // previously enabled it. This does not run while the app is closed.
  useEffect(() => {
    let cancelled = false;

    const settings =
      getPrivacySettings();

    if (!settings.automaticLocation) {
      return undefined;
    }

    startForegroundStayDetection()
      .catch(error => {
        if (cancelled) return;

        const reverted =
          savePrivacySettings({
            ...settings,
            automaticLocation: false
          });

        setPrivacy(reverted);

        setMessage(
          error && error.message
            ? `Automatic stay logging could not start: ${error.message}`
            : 'Automatic stay logging could not start.'
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onStayPersisted = async () => {
      setStays(getPrivateStayEvents());

      const settings = getPrivacySettings();

      if (!settings.shareExposureStays) {
        return;
      }

      try {
        await uploadPendingStayEvents();
        setStays(getPrivateStayEvents());
      } catch (error) {
        // Keep the stay locally. It remains pending and can be
        // retried automatically or with the manual upload button.
        setStays(getPrivateStayEvents());
      }
    };

    window.addEventListener(
      STAY_PERSISTED_EVENT,
      onStayPersisted
    );

    return () => {
      window.removeEventListener(
        STAY_PERSISTED_EVENT,
        onStayPersisted
      );
    };
  }, []);

  const updatePrivacy = async (name, value) => {
    const previous = privacy;

    const next =
      savePrivacySettings({
        ...previous,
        [name]: value
      });

    setPrivacy(next);

    try {
      if (name === 'automaticLocation') {
        if (value) {
          await startForegroundStayDetection();
        } else {
          await stopForegroundStayDetection();
        }

        setStays(
          getPrivateStayEvents()
        );
      }

      if (
        name === 'shareExposureStays' &&
        value
      ) {
        const result =
          await uploadPendingStayEvents();

        setStays(
          getPrivateStayEvents()
        );

        setMessage(
          result.uploaded
            ? `Uploaded ${result.uploaded} detected stay event(s) for private exposure matching.`
            : 'Stay sharing is enabled. No eligible detected stays are waiting to be uploaded.'
        );
      }
    } catch (error) {
      const reverted =
        savePrivacySettings(previous);

      setPrivacy(reverted);

      setMessage(
        error && error.message
          ? error.message
          : 'That privacy setting could not be changed.'
      );
    }
  };

  const useCurrentLocationForManualStay = async () => {
    try {
      const point =
        await getCurrentPositionForManualStay();

      setManualPoint(point);

      setMessage(
        'Current location selected. Adjust the arrival and departure times before saving.'
      );
    } catch (error) {
      setMessage(
        error && error.message
          ? error.message
          : 'Your current location could not be selected.'
      );
    }
  };

  const saveManualStay = async () => {
    if (!manualPoint) {
      setMessage(
        'Choose a place on the map or use your current location first.'
      );
      return;
    }

    const start =
      new Date(manualStart);

    const end =
      new Date(manualEnd);

    if (
      !Number.isFinite(start.getTime()) ||
      !Number.isFinite(end.getTime()) ||
      end <= start
    ) {
      setMessage(
        'Enter a valid arrival and departure time.'
      );
      return;
    }

    const retentionHours =
      Math.min(
        Math.max(
          Number(
            privacy.retainRawLocationHours
          ) || 24,
          1
        ),
        24 * 90
      );

    const oldestEligibleEnd =
      Date.now() -
      retentionHours *
        60 *
        60 *
        1000;

    if (
      privacy.shareExposureStays &&
      end.getTime() <= oldestEligibleEnd
    ) {
      setMessage(
        `That stay is older than your ${retentionLabel(retentionHours)} matching period. Choose a longer retention period if you want this stay to be used for exposure matching.`
      );
      return;
    }

    try {
      const saved =
        addManualStayEvent({
          latitude:
            manualPoint.latitude,
          longitude:
            manualPoint.longitude,
          accuracy:
            manualPoint.accuracy,
          startedAt:
            start,
          endedAt:
            end
        });

      setStays(
        getPrivateStayEvents()
      );

      if (privacy.shareExposureStays) {
        const result =
          await uploadPendingStayEvents();

        setStays(
          getPrivateStayEvents()
        );

        const manualAccepted =
          Array.isArray(
            result.acceptedClientEventIds
          ) &&
          result.acceptedClientEventIds.includes(
            String(saved.stay.id)
          );

        if (manualAccepted) {
          setMessage(
            'Your stay was saved and is now available for private exposure matching.'
          );
        } else if (
          result.reason ===
          'not_authenticated'
        ) {
          setMessage(
            'Your stay was saved on this device. Sign in before using it for exposure matching.'
          );
        } else {
          setMessage(
            'Your stay was saved on this device and is waiting for exposure matching.'
          );
        }
      } else {
        setMessage(
          'Your stay was saved privately on this device. Turn on exposure protection when you want to use recorded stays for matching.'
        );
      }

      setShowManualStay(false);
      setManualPoint(null);

    } catch (error) {
      setMessage(
        error && error.message
          ? error.message
          : 'The stay could not be saved.'
      );
    }
  };

  const enableExposureProtection = async () => {
    const next =
      savePrivacySettings({
        ...privacy,
        shareExposureStays: true,
        exposureNotifications: true
      });

    setPrivacy(next);

    try {
      const result =
        await uploadPendingStayEvents();

      setStays(
        getPrivateStayEvents()
      );

      if (result.uploaded) {
        setMessage(
          `Exposure matching is on. ${result.uploaded} recorded stay(s) were made available for private matching.`
        );
        return;
      }

      setMessage(
        'Exposure matching is on. Add where you were manually, or optionally enable automatic logging while IndexCase is open.'
      );
    } catch (_) {
      setStays(
        getPrivateStayEvents()
      );

      setMessage(
        'Exposure matching is on. A stay that cannot be submitted right now will remain on this device for a later retry.'
      );
    }
  };

  const pauseExposureProtection = async () => {
    const next =
      savePrivacySettings({
        ...privacy,
        shareExposureStays: false
      });

    setPrivacy(next);

    setStays(
      getPrivateStayEvents()
    );

    setMessage(
      privacy.automaticLocation
        ? 'Exposure matching is paused. Automatic logging remains on, but newly recorded stays will remain on this device.'
        : 'Exposure matching is paused. Recorded stays remain private on this device.'
    );
  };

  const uploadPendingStays = async () => {
    try {
      const result =
        await uploadPendingStayEvents();

      setStays(
        getPrivateStayEvents()
      );

      if (result.uploaded) {
        setMessage(
          `Uploaded ${result.uploaded} detected stay event(s) for private exposure matching.`
        );
        return;
      }

      if (result.reason === 'not_authenticated') {
        setMessage(
          'Detected stays remain private on this device until you are signed in.'
        );
        return;
      }

      if (result.rejected) {
        setMessage(
          `${result.rejected} detected stay event(s) were not accepted. They remain private on this device.`
        );
        return;
      }

      setMessage(
        'No eligible detected stays are waiting to be uploaded.'
      );
    } catch (_) {
      setMessage(
        'Detected stays could not be uploaded right now. They remain private on this device.'
      );
    }
  };

  const addEvidence = () => {
    if (!selectedDisease) return;

    const disease =
      diseaseList.find(
        item =>
          String(item.disease_id) ===
          String(selectedDisease)
      );

    const item =
      saveDiseaseEvidence({
        diseaseId:
          selectedDisease,
        diseaseName:
          disease
            ? disease.name
            : selectedDisease,
        level:
          EVIDENCE_LEVELS.SELF_REPORTED,
        verificationStatus:
          'self_reported',
      });

    setEvidence([
      ...evidence,
      item
    ]);

    setSelectedDisease('');

    setMessage(
      'Saved privately on this device. Nothing was submitted to your IndexCase account yet.'
    );
  };

  const submitLocalEvidence = async item => {
    if (
      !item ||
      item.serverBacked ||
      item.level !==
        EVIDENCE_LEVELS.SELF_REPORTED
    ) {
      return;
    }

    setSubmittingEvidenceId(
      item.id
    );

    try {
      const next =
        await submitDiseaseEvidenceToAccount(
          item
        );

      setEvidence(
        next
      );

      setMessage(
        'Your report is now stored in your IndexCase account. It remains a self-report and is not independently verified.'
      );
    } catch (error) {
      setMessage(
        error &&
        error.message
          ? error.message
          : 'The self-report could not be submitted. It remains private on this device.'
      );
    } finally {
      setSubmittingEvidenceId(
        null
      );
    }
  };

  const evidenceDisplay = item => {
    const level =
      evidenceLabels[item.level] ||
      'Disease evidence';

    const verified =
      item.verificationStatus ===
        'verified' ||
      item.verification_status ===
        'verified';

    if (verified) {
      return {
        label: level,
        status: 'Verified',
        color: 'success'
      };
    }

    if (
      item.level ===
      EVIDENCE_LEVELS.SELF_REPORTED
    ) {
      return {
        label: 'Self reported',
        status: 'Not independently verified',
        color: 'secondary'
      };
    }

    return {
      label: level,
      status: 'Pending verification',
      color: 'secondary'
    };
  };

  return (
    <div className="health-safety">
      <div className="mb-4">
        <h2 className="mb-1">
          Health &amp; Safety
        </h2>

        <p className="text-muted mb-0">
          Sharing where you have been helps IndexCase warn you when
          you may have crossed paths with someone whose infection
          report qualifies for exposure matching. Reporting an
          infection can help warn other users without revealing your
          identity or unrelated location history.
        </p>
      </div>

      {message && (
        <Alert
          color="info"
          role="status"
        >
          {message}
        </Alert>
      )}

      <Card className="mb-3">
        <CardBody>
          <Row>
            <Col lg="8">
              <CardTitle
                tag="h3"
                className="mb-2"
              >
                Get warned about possible exposures
              </CardTitle>

              <p className="mb-3">
                IndexCase can compare the places and times you choose
                to share with qualifying infection reports. If a
                disease-specific exposure rule finds a match, you can
                receive a possible-exposure alert.
              </p>


              <FormGroup>
                <Label for="exposureRetention">
                  For newly shared stays, keep them available for matching for
                </Label>

                <Input
                  id="exposureRetention"
                  type="select"
                  value={
                    Number(
                      privacy.retainRawLocationHours
                    ) || 24
                  }
                  onChange={e =>
                    updatePrivacy(
                      'retainRawLocationHours',
                      Number(e.target.value)
                    )
                  }
                >
                  <option value="24">1 day</option>
                  <option value="72">3 days</option>
                  <option value="168">7 days</option>
                  <option value="336">14 days</option>
                  <option value="720">30 days</option>
                  <option value="2160">90 days</option>
                </Input>

                <div className="small text-muted mt-1">
                  A longer period gives IndexCase more time to match
                  an infection report made later. This setting applies
                  when a stay is shared; changing it does not alter
                  the retention period of stays already uploaded.
                </div>
              </FormGroup>

              {exposureProtectionEnabled ? (
                <>
                  <Alert
                    color="success"
                    className="mb-3"
                  >
                    <strong>
                      Exposure matching is on.
                    </strong>
                    {' '}
                    Places you add manually, and any stays you
                    optionally record automatically, can be used for
                    private exposure matching.
                  </Alert>

                  <Button
                    outline
                    color="secondary"
                    onClick={
                      pauseExposureProtection
                    }
                  >
                    Pause exposure matching
                  </Button>
                </>
              ) : (
                <>
                  <Alert
                    color="secondary"
                    className="mb-3"
                  >
                    <strong>
                      Exposure matching is off.
                    </strong>
                    {' '}
                    Enable matching to let the stays you choose to
                    share be checked against qualifying infection
                    reports. Automatic location logging is optional.
                  </Alert>

                  <Button
                    color="primary"
                    onClick={
                      enableExposureProtection
                    }
                  >
                    Enable exposure matching
                  </Button>
                </>
              )}
            </Col>

            <Col
              lg="4"
              className="mt-3 mt-lg-0"
            >
              <div className="border rounded p-3 h-100">
                <strong>
                  What this does
                </strong>

                <div className="small mt-2">
                  ✓ Add places manually without granting location permission
                </div>

                <div className="small mt-2">
                  ✓ Use shared stays for private exposure matching
                </div>

                <div className="small mt-2">
                  ✓ See alerts when a qualifying possible exposure is found
                </div>

                <div className="small text-muted mt-3">
                  Automatic logging is optional. If enabled, it works
                  only while IndexCase is open in this build.
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Row className="mb-3">
        <Col
          xs="6"
          lg="3"
          className="mb-2"
        >
          <div className="border rounded p-3 h-100">
            <div className="small text-muted">
              Exposure alerts
            </div>
            <div className="h4 mb-0">
              {warnings.length}
            </div>
          </div>
        </Col>

        <Col
          xs="6"
          lg="3"
          className="mb-2"
        >
          <div className="border rounded p-3 h-100">
            <div className="small text-muted">
              Places recorded
            </div>
            <div className="h4 mb-0">
              {stays.length}
            </div>
          </div>
        </Col>

        <Col
          xs="6"
          lg="3"
          className="mb-2"
        >
          <div className="border rounded p-3 h-100">
            <div className="small text-muted">
              Waiting for matching
            </div>
            <div className="h4 mb-0">
              {pendingStayCount}
            </div>
          </div>
        </Col>

        <Col
          xs="6"
          lg="3"
          className="mb-2"
        >
          <div className="border rounded p-3 h-100">
            <div className="small text-muted">
              Provider access
            </div>
            <div className="h4 mb-0">
              {activeConsents.length}
            </div>
          </div>
        </Col>
      </Row>

      <Card className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <CardTitle
              tag="h4"
              className="mb-0"
            >
              Possible exposure warnings
            </CardTitle>

            <Badge
              color={
                warnings.length
                  ? 'warning'
                  : 'success'
              }
              pill
            >
              {warnings.length
                ? warnings.length
                : 'None'}
            </Badge>
          </div>

          {!warnings.length && (
            <div>
              <strong>
                {exposureProtectionEnabled
                  ? 'No possible exposures found'
                  : 'Exposure checking is not fully enabled'}
              </strong>

              <p className="text-muted mb-0 mt-1">
                {exposureProtectionEnabled
                  ? 'IndexCase has not found a qualifying match between the stays you shared and an infection report.'
                  : 'Enable exposure matching, then add where you were manually or optionally turn on automatic logging.'}
              </p>
            </div>
          )}

          {warnings.map(w => (
            <Alert
              color={
                w.serious &&
                w.symptomMatch
                  ? 'warning'
                  : 'info'
              }
              key={
                w.id ||
                `${w.disease}-${w.exposureAt}`
              }
              className="mb-2"
            >
              <strong>
                Possible exposure to{' '}
                {w.disease ||
                  'a reported disease'}
              </strong>

              {w.exposureAt && (
                <>
                  <br />
                  Around{' '}
                  {new Date(
                    w.exposureAt
                  ).toLocaleString()}
                </>
              )}

              {Array.isArray(
                w.expectedSymptoms
              ) &&
                w.expectedSymptoms.length >
                  0 && (
                  <>
                    <br />
                    Watch for:{' '}
                    {w.expectedSymptoms.join(
                      ', '
                    )}
                    .
                  </>
                )}

              <div className="mt-2">
                {warningActionText(w)}
              </div>

              <div className="small mt-2 text-muted">
                IndexCase does not disclose the identity or unrelated
                movement history of the other person.
              </div>
            </Alert>
          ))}
        </CardBody>
      </Card>

      <Row>
        <Col lg="7">
          <Card className="mb-3 h-100">
            <CardBody>
              <CardTitle tag="h4">
                Where I have been &amp; privacy
              </CardTitle>

              <p className="text-muted">
                The places and times you choose to share let IndexCase
                privately check whether you overlapped with a
                qualifying infection report. Automatic detection
                works only while IndexCase is open in this build.
              </p>


              <div className="border rounded p-3 mb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <strong>
                      Add where I was
                    </strong>

                    <div className="small text-muted">
                      Pick a place and enter when you were there.
                    </div>
                  </div>

                  <Button
                    size="sm"
                    outline
                    color="primary"
                    onClick={() =>
                      setShowManualStay(
                        !showManualStay
                      )
                    }
                  >
                    {showManualStay
                      ? 'Cancel'
                      : 'Add a stay'}
                  </Button>
                </div>

                {showManualStay && (
                  <div className="mt-3">
                    <p className="small mb-2">
                      Click the map where you were, or use your current
                      location as a starting point.
                    </p>

                    <MapContainer
                      key={
                        manualPoint
                          ? `manual-${manualPoint.latitude}-${manualPoint.longitude}`
                          : 'manual-world'
                      }
                      center={
                        manualPoint
                          ? [
                              manualPoint.latitude,
                              manualPoint.longitude
                            ]
                          : [20, 0]
                      }
                      zoom={
                        manualPoint
                          ? 15
                          : 2
                      }
                      scrollWheelZoom
                      style={{
                        height: '280px',
                        width: '100%'
                      }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />

                      <ManualStayMapSelection
                        point={manualPoint}
                        onSelect={setManualPoint}
                      />
                    </MapContainer>

                    <div className="mt-2">
                      <Button
                        size="sm"
                        outline
                        onClick={
                          useCurrentLocationForManualStay
                        }
                      >
                        Use my current location
                      </Button>

                      {manualPoint && (
                        <span className="small text-success ml-2">
                          Location selected
                        </span>
                      )}
                    </div>

                    <Row className="mt-3">
                      <Col md="6">
                        <Label for="manualStayStart">
                          Arrived
                        </Label>

                        <Input
                          id="manualStayStart"
                          type="datetime-local"
                          value={manualStart}
                          onChange={e =>
                            setManualStart(
                              e.target.value
                            )
                          }
                        />
                      </Col>

                      <Col md="6">
                        <Label for="manualStayEnd">
                          Left
                        </Label>

                        <Input
                          id="manualStayEnd"
                          type="datetime-local"
                          value={manualEnd}
                          onChange={e =>
                            setManualEnd(
                              e.target.value
                            )
                          }
                        />
                      </Col>
                    </Row>

                    <div className="small text-muted mt-2">
                      Current matching period:{' '}
                      {retentionLabel(
                        privacy.retainRawLocationHours ||
                        24
                      )}.
                    </div>

                    <Button
                      className="mt-3"
                      color="primary"
                      disabled={!manualPoint}
                      onClick={saveManualStay}
                    >
                      {privacy.shareExposureStays
                        ? 'Save and use for matching'
                        : 'Save privately'}
                    </Button>
                  </div>
                )}
              </div>

              <FormGroup
                check
                switch
              >
                <Input
                  type="switch"
                  checked={
                    !!privacy.automaticLocation
                  }
                  onChange={e =>
                    updatePrivacy(
                      'automaticLocation',
                      e.target.checked
                    )
                  }
                />

                <Label check>
                  Automatically record meaningful stays while IndexCase is open
                </Label>
              </FormGroup>

              <FormGroup
                check
                switch
              >
                <Input
                  type="switch"
                  checked={
                    !!privacy.shareExposureStays
                  }
                  onChange={e =>
                    updatePrivacy(
                      'shareExposureStays',
                      e.target.checked
                    )
                  }
                />

                <Label check>
                  Use recorded stays for private exposure matching
                </Label>
              </FormGroup>

              <div className="border rounded p-3 my-3">
                <div className="d-flex flex-wrap justify-content-between">
                  <span>
                    Recorded on this device
                  </span>
                  <strong>
                    {pendingStayCount}
                  </strong>
                </div>

                <div className="d-flex flex-wrap justify-content-between mt-1">
                  <span>
                    Available for matching
                  </span>
                  <strong>
                    {uploadedStayCount}
                  </strong>
                </div>

                <div className="small text-muted mt-2">
                  Uploaded raw stays remain subject to the configured
                  retention period. Internal shared-space identifiers
                  are not shown here.
                </div>
              </div>

              <Button
                size="sm"
                outline
                disabled={
                  !privacy.shareExposureStays ||
                  pendingStayCount === 0
                }
                onClick={
                  uploadPendingStays
                }
              >
                Check pending stays now
              </Button>

              {recentStays.length > 0 && (
                <div className="mt-3">
                  <strong>
                    Recent detected stays
                  </strong>

                  <div className="small text-muted mb-2">
                    Times are shown for your own review. Coordinates are
                    intentionally not displayed on this overview.
                  </div>

                  {recentStays.map(stay => (
                    <div
                      key={stay.id}
                      className="border-top py-2 d-flex justify-content-between align-items-start"
                    >
                      <div className="small">
                        {stay.startedAt
                          ? new Date(
                              stay.startedAt
                            ).toLocaleString()
                          : 'Unknown start'}

                        {' – '}

                        {stay.endedAt
                          ? new Date(
                              stay.endedAt
                            ).toLocaleString()
                          : 'Unknown end'}
                      </div>

                      <Badge
                        color={
                          stay.uploaded
                            ? 'success'
                            : 'secondary'
                        }
                        className="ml-2"
                      >
                        {stay.uploaded
                          ? 'Shared for matching'
                          : 'Private on device'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}

              <hr />

              <FormGroup
                check
                switch
              >
                <Input
                  type="switch"
                  checked={
                    !!privacy.exposureNotifications
                  }
                  onChange={e =>
                    updatePrivacy(
                      'exposureNotifications',
                      e.target.checked
                    )
                  }
                />

                <Label check>
                  Notify me when a possible exposure is found
                </Label>
              </FormGroup>

              <FormGroup
                check
                switch
                className="mb-0"
              >
                <Input
                  type="switch"
                  checked={
                    !!privacy.hotspotWarnings
                  }
                  onChange={e =>
                    updatePrivacy(
                      'hotspotWarnings',
                      e.target.checked
                    )
                  }
                />

                <Label check>
                  Show privacy-preserving hotspot warnings
                </Label>
              </FormGroup>

              <p className="small text-muted mt-3 mb-0">
                Background tracking while IndexCase is closed is not
                enabled by this build.
              </p>
            </CardBody>
          </Card>
        </Col>

        <Col lg="5">
          <Card className="mb-3 h-100">
            <CardBody>
              <CardTitle tag="h4">
                Report an infection
              </CardTitle>

              <p className="text-muted">
                Save a self-report privately first. If you choose to
                submit it to your IndexCase account, the report can
                participate in the governed exposure workflow when it
                qualifies under the applicable disease rule. A
                self-report is not independently verified.
              </p>

              <Alert color="light">
                A new self-report saved here stays private on this
                device. Evidence already stored in your IndexCase
                account is refreshed separately. Clinician or laboratory
                confirmation requires a trusted verification workflow.
              </Alert>

              <Label for="healthDisease">
                Disease
              </Label>

              <Input
                id="healthDisease"
                type="select"
                value={selectedDisease}
                onChange={e =>
                  setSelectedDisease(
                    e.target.value
                  )
                }
              >
                <option value="">
                  Choose disease
                </option>

                {diseaseList.map(d => (
                  <option
                    key={d.disease_id}
                    value={d.disease_id}
                  >
                    {d.name}
                  </option>
                ))}
              </Input>

              <Button
                className="mt-2"
                onClick={addEvidence}
                disabled={!selectedDisease}
              >
                Save privately
              </Button>

              <div className="mt-3">
                {!evidence.length && (
                  <p className="text-muted">
                    No disease evidence is stored on this device.
                  </p>
                )}

                {evidence.map(item => {
                  const display =
                    evidenceDisplay(item);

                  return (
                    <div
                      key={item.id}
                      className="border-top py-2"
                    >
                      <div>
                        <strong>
                          {item.diseaseName}
                        </strong>
                      </div>

                      <div className="small">
                        {display.label}
                      </div>

                      <Badge
                        color={
                          display.color
                        }
                      >
                        {display.status}
                        {' · '}
                        {item.serverBacked
                          ? 'Stored in account'
                          : 'Private on device'}
                      </Badge>
                  {!item.serverBacked &&
                    item.level ===
                      EVIDENCE_LEVELS.SELF_REPORTED && (
                      <Button
                        size="sm"
                        color="primary"
                        outline
                        className="ml-2 mt-1"
                        disabled={
                          submittingEvidenceId ===
                          item.id
                        }
                        onClick={() =>
                          submitLocalEvidence(
                            item
                          )
                        }
                      >
                        {submittingEvidenceId ===
                        item.id
                          ? 'Submitting...'
                          : 'Submit to my IndexCase account'}
                      </Button>
                    )}

                    </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Card className="mt-3 mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <CardTitle
                tag="h4"
                className="mb-1"
              >
                Healthcare access
              </CardTitle>

              <p className="text-muted">
                Provider access must be explicitly authorized and can be
                revoked. Public-health access is a separate governed
                server-side workflow.
              </p>
            </div>

            <Badge
              color={
                activeConsents.length
                  ? 'primary'
                  : 'secondary'
              }
              pill
            >
              {activeConsents.length}
            </Badge>
          </div>

          {!activeConsents.length && (
            <p className="text-muted mb-0">
              No active provider access grants.
            </p>
          )}

          {activeConsents.map(grant => (
            <div
              key={grant.id}
              className="border rounded p-3 mb-2"
            >
              <strong>
                {grant.providerName ||
                  'Authorized provider'}
              </strong>

              <div className="small mt-1 mb-2">
                {(
                  Array.isArray(
                    grant.scopes
                  )
                    ? grant.scopes
                    : []
                )
                  .map(
                    scope =>
                      (
                        CONSENT_SCOPES.find(
                          item =>
                            item.id ===
                            scope
                        ) || {
                          label: scope
                        }
                      ).label
                  )
                  .join(', ')}
              </div>

              <Button
                size="sm"
                color="danger"
                outline
                onClick={async () => {
                  try {
                    const next =
                      await revokeConsentGrant(
                        grant.id
                      );

                    setConsents(
                      next
                    );

                    setMessage(
                      'Provider access was revoked.'
                    );
                  } catch (_) {
                    setMessage(
                      'Provider access could not be revoked right now.'
                    );
                  }
                }}
              >
                Revoke access
              </Button>
            </div>
          ))}
        </CardBody>
      </Card>
    </div>
  );
}
